import React from 'react';
import { Check, Plus } from 'lucide-react';
import { Button } from '../../ui/Button';
import { TableColumn, SubfieldDefinition } from '../../../types/domain';
import { SchemaColumnRow } from './SchemaColumnRow';
import { SchemaSubfieldRow } from './SchemaSubfieldRow';

interface SchemaAttributesTableProps {
  filteredColumns: TableColumn[];
  searchFilter: string;
  canEdit: boolean;
  draggedColId: string | null;
  expandedRowIds: Set<string>;
  hasChanges: boolean;
  isSavingChanges: boolean;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onToggleExpand: (id: string) => void;
  onUpdateField: (colId: string, field: keyof TableColumn, value: any) => void;
  onToggleRequired: (colId: string) => void;
  onUpdateValidationRules: (colId: string, patch: Record<string, any>) => void;
  onUpdateSampleValue: (colId: string, sampleValue: string) => void;
  onAddSubfield: (colId: string) => void;
  onDeleteColumn: (col: TableColumn) => void;
  onUpdateSubfield: (colId: string, subfieldId: string, field: keyof SubfieldDefinition, value: any) => void;
  onDeleteSubfield: (colId: string, subfieldId: string) => void;
  onSaveChanges: () => void;
  onAddNewAttribute: () => void;
}

export const SchemaAttributesTable: React.FC<SchemaAttributesTableProps> = ({
  filteredColumns,
  searchFilter,
  canEdit,
  draggedColId,
  expandedRowIds,
  hasChanges,
  isSavingChanges,
  onDragStart,
  onDragOver,
  onDrop,
  onToggleExpand,
  onUpdateField,
  onToggleRequired,
  onUpdateValidationRules,
  onUpdateSampleValue,
  onAddSubfield,
  onDeleteColumn,
  onUpdateSubfield,
  onDeleteSubfield,
  onSaveChanges,
  onAddNewAttribute,
}) => {
  return (
    <div className="rounded-lg border border-cf-border bg-cf-surface overflow-hidden shadow-xs font-sans text-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[1080px]">
          <thead>
            <tr className="border-b border-cf-border bg-cf-bg/70 text-cf-textMuted uppercase font-sans text-xs tracking-wider select-none">
              {/* Dedicated Column 1: Navigation and Drag */}
              <th className="py-2.5 px-2 font-semibold w-12 min-w-[48px] max-w-[48px] text-center">#</th>
              {/* Dedicated Column 2: Field Label */}
              <th className="py-2.5 px-3 font-semibold w-[240px] min-w-[220px]">FIELD LABEL</th>
              {/* Dedicated Column 3: Field Key */}
              <th className="py-2.5 px-3 font-semibold w-[180px] min-w-[160px]">FIELD KEY</th>
              {/* Column 4: Data Type */}
              <th className="py-2.5 px-3 font-semibold w-[180px] min-w-[160px]">DATA TYPE</th>
              {/* Column 5: Requirement */}
              <th className="py-2.5 px-3 font-semibold w-[120px] min-w-[110px] text-center">REQUIREMENT</th>
              {/* Column 6: Sample / Constraints */}
              <th className="py-2.5 px-3 font-semibold w-[270px] min-w-[260px]">SAMPLE / CONSTRAINTS</th>
              {/* Column 7: Actions */}
              <th className="py-2.5 px-3 font-semibold w-[110px] min-w-[100px] text-right">ACTIONS</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-cf-border/60">
            {filteredColumns.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-cf-textMuted text-xs">
                  {searchFilter
                    ? `No fields match "${searchFilter}".`
                    : 'No explicit schema fields. Click "+ Add New Attribute" on the right to configure columns.'}
                </td>
              </tr>
            ) : (
              filteredColumns.map((col) => {
                const isExpanded = expandedRowIds.has(col.id);
                const subfields: SubfieldDefinition[] = col.validation_rules?.subfields || [];

                return (
                  <React.Fragment key={col.id}>
                    {/* Top-Level Column Row */}
                    <SchemaColumnRow
                      col={col}
                      canEdit={canEdit}
                      draggedColId={draggedColId}
                      isExpanded={isExpanded}
                      onDragStart={onDragStart}
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                      onToggleExpand={onToggleExpand}
                      onUpdateField={onUpdateField}
                      onToggleRequired={onToggleRequired}
                      onUpdateValidationRules={onUpdateValidationRules}
                      onUpdateSampleValue={onUpdateSampleValue}
                      onAddSubfield={onAddSubfield}
                      onDeleteColumn={onDeleteColumn}
                    />

                    {/* Subfield Rows */}
                    {isExpanded &&
                      subfields.map((sub) => (
                        <SchemaSubfieldRow
                          key={sub.id}
                          col={col}
                          sub={sub}
                          canEdit={canEdit}
                          onUpdateSubfield={onUpdateSubfield}
                          onDeleteSubfield={onDeleteSubfield}
                        />
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Table Footer Controls (Add New Attribute on RIGHT) */}
      <div className="flex items-center justify-between p-3 border-t border-cf-border bg-cf-bg/50 flex-wrap gap-2">
        <span className="text-xs text-cf-textMuted font-sans select-none">
          Drag rows to rearrange column ordering · Subfields can be nested under Object &amp; Array types
        </span>

        <div className="flex items-center gap-2 ml-auto">
          {hasChanges && (
            <Button
              variant="primary"
              size="xs"
              disabled={isSavingChanges}
              isLoading={isSavingChanges}
              onClick={onSaveChanges}
              leftIcon={!isSavingChanges ? <Check size={12} /> : undefined}
            >
              Save Changes
            </Button>
          )}
          {canEdit && (
            <Button
              variant="secondary"
              size="xs"
              onClick={onAddNewAttribute}
              leftIcon={<Plus size={13} className="text-cf-orange" />}
            >
              Add New Attribute
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
